import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, OnInit} from '@angular/core';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {NgTemplateOutlet} from '@angular/common';
import {MatCard, MatCardContent} from '@angular/material/card';
import {isDefined, isUndefined} from '@aviary/store/common/typing.helpers';

type ColorGridPage = 'main-theme' | 'semantic-aliases' | 'other-figma' | 'reference' | 'material';

interface ColorSwatchPair {
  colorName: string;
  color?: ColorSwatch;
  onColor?: ColorSwatch;
}

interface ColorSwatchGroup {
  label: string;
  pairs: ColorSwatchPair[];
}

interface ColorSwatch {
  label: string;
  tokenName: string;
  tailwindName: string;
  figmaName?: string;
  available?: boolean;
}

interface ColorPalette {
  label: string;
  swatches: ColorSwatch[];
}

const REFERENCE_PALETTE_NAMES: string[] = [
  'primary',
  'secondary',
  'tertiary',
  'error',
  'neutral',
  'neutral-variant'
];

const REFERENCE_TONES: number[] = [
  0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 99, 100
];

const MATERIAL_PALETTE_NAMES: string[] = [
  'red',
  'pink',
  'purple',
  'deep-purple',
  'indigo',
  'blue',
  'light-blue',
  'cyan',
  'teal',
  'green',
  'light-green',
  'lime',
  'yellow',
  'amber',
  'orange',
  'deep-orange',
  'brown',
  'grey',
  'blue-grey'
];

const MATERIAL_SHADES: (number | string)[] = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900,
  'A100', 'A200', 'A400', 'A700'
];

const MATERIAL_PALETTES_WITHOUT_ACCENTS: string[] = [
  'brown',
  'grey',
  'blue-grey'
];

const MAIN_THEME_COLOR_NAMES: string[] = [
  'primary',
  'on-primary',
  'primary-container',
  'on-primary-container',

  'secondary',
  'on-secondary',
  'secondary-container',
  'on-secondary-container',

  'tertiary',
  'on-tertiary',
  'tertiary-container',
  'on-tertiary-container',

  'error',
  'on-error',
  'error-container',
  'on-error-container',

  'background',
  'on-background',

  'surface',
  'on-surface',
  'surface-variant',
  'on-surface-variant',

  'outline',
  'outline-variant',

  'surface1',
  'surface2',
  'surface3',
  'surface4',
  'surface5'
];

const COLOR_TOKEN_PREFIX: string = '--mx-color-';

@Component({
  selector: 'mx-component-lib-color-grid',
  host: {
    class: 'component-lib-content'
  },
  templateUrl: './component-lib-color-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatRadioGroup, MatRadioButton, NgTemplateOutlet, MatCard, MatCardContent]
})
export class ComponentLibColorGridComponent implements OnInit, AfterViewInit {

  @Input({required: true}) public figmaColorPrefix: string;
  @Input({required: true}) public currentTheme: 'light' | 'dark';
  @Input() public colorAliasNames: readonly string[] = [];

  public readonly pages = [
    {
      value: 'main-theme',
      label: 'Main Theme',
      description: 'App-specific main theme colors.',
      source: 'Figma "CSS Variables Generator" plugin'
    },
    {
      value: 'semantic-aliases',
      label: 'Semantic Aliases',
      description: 'Purpose-specific aliases for existing colors that don\'t already have semantic names in Figma.',
      source: 'color-aliases.scss'
    },
    {
      value: 'other-figma',
      label: 'Other Figma Exports',
      description: 'Additional colors exported from Figma that aren\'t in Main Theme or Reference Palettes.',
      source: 'Figma "CSS Variables Generator" plugin'
    },
    {
      value: 'reference',
      label: 'Reference Palettes',
      description: 'App-specific reference palettes used to build the application theme.',
      source: 'Figma "CSS Variables Generator" plugin'
    },
    {
      value: 'material',
      label: 'Material Palettes',
      description: 'App-independent Material Design 2014 palettes.',
      source: 'Angular Material, via material-2014-color-exports.scss'
    }
  ] as const;

  public readonly mainThemeRows: string[][] = [
    ['primary', 'on-primary', 'primary-container', 'on-primary-container'],
    ['secondary', 'on-secondary', 'secondary-container', 'on-secondary-container'],
    ['tertiary', 'on-tertiary', 'tertiary-container', 'on-tertiary-container'],
    ['error', 'on-error', 'error-container', 'on-error-container'],

    ['background', 'on-background', 'surface', 'on-surface'],
    ['surface-variant', 'on-surface-variant', 'outline', 'outline-variant']
  ];

  public readonly mainThemeSurfaceNames: string[] = [
    'surface',
    'surface1',
    'surface2',
    'surface3',
    'surface4',
    'surface5'
  ];

  public selectedPage: ColorGridPage = 'main-theme';

  public referencePalettes: ColorPalette[];
  public materialPalettes: ColorPalette[];

  public mainThemeColors: ColorSwatch[] = [];
  public semanticAliasGroups: ColorSwatchGroup[] = [];
  public otherFigmaColors: ColorSwatch[] = [];
  public otherFigmaPairs: ColorSwatchPair[] = [];

  public selectedSwatch?: ColorSwatch;

  public get selectedPageDescription(): string {
    return this.getSelectedPageProperty('description');
  }

  public get selectedPageSource(): string {
    return this.getSelectedPageProperty('source');
  }

  public get selectedTokenName(): string {
    return this.selectedSwatch?.tokenName ?? '';
  }

  public get selectedTailwindName(): string {
    return this.selectedSwatch?.tailwindName ?? '';
  }

  public get selectedFigmaName(): string {
    if (isUndefined(this.selectedSwatch)) return '';

    switch (this.selectedPage) {
      case 'main-theme':
        return `${this.figmaColorPrefix}/sys/${this.currentTheme}/${this.colorNameFor(this.selectedSwatch.tokenName)}`;

      case 'semantic-aliases':
        return '(unnamed in Figma)';

      case 'other-figma':
        return '(unknown)';

      default:
        return this.selectedSwatch.figmaName ?? '';
    }
  }

  public get selectedRgbValue(): string {
    return this.computedValueForToken(this.selectedSwatch?.tokenName);
  }

  private readonly element: HTMLElement = inject(ElementRef<HTMLElement>).nativeElement;
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    this.referencePalettes = REFERENCE_PALETTE_NAMES.map(colorName => ({
      label: this.displayNameFor(colorName),
      swatches: REFERENCE_TONES.map(tone => ({
        label: String(tone),
        tokenName: `--mx-ref-${colorName}-${tone}`,
        tailwindName: `ref-${colorName}-${tone}`,
        figmaName: `${this.figmaColorPrefix}/ref/${colorName}/${colorName}${tone}`
      }))
    }));

    this.materialPalettes = MATERIAL_PALETTE_NAMES.map(colorName => ({
      label: this.displayNameFor(colorName),
      swatches: MATERIAL_SHADES.map(shade => ({
        label: String(shade).replace('A', 'A '),
        tokenName: `--mx-mat-${colorName}-${shade}`.toLowerCase(),
        tailwindName: `mat-${colorName}-${shade}`.toLowerCase(),
        figmaName: `${this.displayNameFor(colorName)} / ${String(shade).replace('A', 'A ')}`,
        available: !MATERIAL_PALETTES_WITHOUT_ACCENTS.includes(colorName) || !String(shade).startsWith('A')
      }))
    }));

    this.mainThemeColors = MAIN_THEME_COLOR_NAMES.map(colorName => this.colorSwatchFor(colorName));

    this.changeDetectorRef.markForCheck();
  }

  public ngAfterViewInit(): void {
    const mainThemeColorNames: Set<string> = new Set(MAIN_THEME_COLOR_NAMES);
    const semanticAliasColorNames: Set<string> = new Set(this.colorAliasNames);

    this.semanticAliasGroups = this.buildSemanticAliasGroups();

    this.otherFigmaColors = this.allMxColorTokenNames()
      .map(tokenName => this.colorNameFor(tokenName))
      .filter(colorName => !mainThemeColorNames.has(colorName))
      .filter(colorName => !semanticAliasColorNames.has(colorName))
      .sort()
      .map(colorName => this.colorSwatchFor(colorName));

    this.otherFigmaPairs = this.buildColorPairs(this.otherFigmaColors);

    this.changeDetectorRef.detectChanges();
  }

  public onClickContainer(): void {
    this.selectedSwatch = undefined;
  }

  public onClickSwatch(event: MouseEvent, swatch: ColorSwatch): void {
    event.stopPropagation();

    this.selectedSwatch = this.isSelected(swatch) ? undefined : swatch;
  }

  public isSelected(swatch: ColorSwatch): boolean {
    return this.selectedSwatch?.tokenName === swatch.tokenName;
  }

  public cssColorFor(tokenName: string): string {
    return `rgb(var(${tokenName}))`;
  }

  public mainThemeSwatchFor(colorName: string): ColorSwatch | undefined {
    return this.mainThemeColors.find(swatch => swatch.tokenName === this.tokenNameFor(colorName));
  }

  private getSelectedPageProperty(propertyName: keyof typeof this.pages[0]): string {
    return this.pages.find(page => page.value === this.selectedPage)?.[propertyName] ?? '';
  }

  private allMxColorTokenNames(): string[] {
    const styles: CSSStyleDeclaration = getComputedStyle(this.element);

    return Array.from(styles)
      .filter(propertyName => propertyName.startsWith(COLOR_TOKEN_PREFIX))
      .filter(tokenName => this.tokenExists(tokenName));
  }

  private tokenExists(tokenName: string): boolean {
    return !!this.computedValueForToken(tokenName);
  }

  private computedValueForToken(tokenName?: string): string {
    return isDefined(tokenName) ? getComputedStyle(this.element).getPropertyValue(tokenName).trim() : '';
  }

  private displayNameFor(colorName: string): string {
    const surfaceMatch: RegExpMatchArray | null = colorName.match(/^surface([1-5])$/);

    // Surface colors have special display names.
    if (surfaceMatch) return `Surface at +${surfaceMatch[1]}`;

    return colorName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private colorSwatchFor(colorName: string): ColorSwatch {
    return {
      label: this.displayNameFor(colorName),
      tokenName: this.tokenNameFor(colorName),
      tailwindName: colorName
    };
  }

  private colorNameFor(tokenName: string): string {
    return tokenName.replace(`${COLOR_TOKEN_PREFIX}`, '');
  }

  private tokenNameFor(colorName: string): string {
    return `${COLOR_TOKEN_PREFIX}${colorName}`;
  }

  private prefixesFor(colorName: string): string[] {
    const parts: string[] = colorName.split('-');

    return parts
      .slice(0, -1)
      .map((_, index) => parts.slice(0, index + 1).join('-'))
      .reverse();
  }

  private groupNameFor(pair: ColorSwatchPair, pairs: ColorSwatchPair[]): string | undefined {
    return this.prefixesFor(pair.colorName).find(prefix =>
      pairs.some(other =>
        other !== pair &&
        (other.colorName === prefix || other.colorName.startsWith(`${prefix}-`))
      )
    );
  }

  private buildSemanticAliasGroups(): ColorSwatchGroup[] {
    const pairs: ColorSwatchPair[] = this.buildSemanticAliasPairs();

    // First determine the naturally derived group for every pair.
    const derivedGroupNames: (string | undefined)[] = pairs.map(pair =>
      this.groupNameFor(pair, pairs)
    );

    // A derived group only deserves its own heading if it contains multiple pairs.
    const groupCounts: Map<string, number> = new Map();

    for (const groupName of derivedGroupNames) {
      if (groupName) {
        groupCounts.set(groupName, (groupCounts.get(groupName) ?? 0) + 1);
      }
    }

    const groups: Map<string, ColorSwatchPair[]> = new Map();

    pairs.forEach((pair, index) => {
      const derivedGroupName: string | undefined = derivedGroupNames[index];

      const groupName: string =
        derivedGroupName && (groupCounts.get(derivedGroupName) ?? 0) > 1
          ? this.displayNameFor(derivedGroupName)
          : 'Other';

      const group: ColorSwatchPair[] = groups.get(groupName) ?? [];
      group.push(pair);
      groups.set(groupName, group);
    });

    const result: ColorSwatchGroup[] = Array.from(
      groups,
      ([label, groupPairs]) => ({
        label,
        pairs: groupPairs
      })
    );

    // "Other" is useful, but it should always be the last thing on the page.
    return [
      ...result.filter(group => group.label !== 'Other'),
      ...result.filter(group => group.label === 'Other')
    ];
  }

  private buildSemanticAliasPairs(): ColorSwatchPair[] {
    const pairs: ColorSwatchPair[] = [];
    const processedNames: Set<string> = new Set();

    for (const aliasName of this.colorAliasNames) {
      const colorName: string = aliasName.replace(/^on-/, '');

      if (processedNames.has(colorName)) continue;

      processedNames.add(colorName);

      pairs.push({
        colorName,
        color: this.swatchForColorName(colorName),
        onColor: this.swatchForColorName(`on-${colorName}`)
      });
    }

    return pairs;
  }

  private buildColorPairs(swatches: ColorSwatch[]): ColorSwatchPair[] {
    const swatchesByName: Map<string, ColorSwatch> = new Map(
      swatches.map(swatch => [
        this.colorNameFor(swatch.tokenName),
        swatch
      ])
    );

    const pairs: ColorSwatchPair[] = [];
    const processedNames: Set<string> = new Set();

    for (const swatch of swatches) {
      const colorName: string = this.colorNameFor(swatch.tokenName);
      const baseName: string = colorName.replace(/^on-/, '');

      if (processedNames.has(baseName)) continue;

      processedNames.add(baseName);

      pairs.push({
        colorName: baseName,
        color: swatchesByName.get(baseName),
        onColor: swatchesByName.get(`on-${baseName}`)
      });
    }

    return pairs;
  }

  private swatchForColorName(colorName: string): ColorSwatch | undefined {
    return this.tokenExists(this.tokenNameFor(colorName)) ? this.colorSwatchFor(colorName) : undefined;
  }
}
